import type { EncounterModel } from '@ctw/shared/api/fhir/models/encounter';
import type { ObservationModel } from '@ctw/shared/api/fhir/models/observation';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import { fetchObservationsByLoinc } from '@ctw/shared/api/fhir/observations';
import { SYSTEM_LOINC } from '@ctw/shared/api/fhir/system-urls';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import type { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import type { Env } from '@ctw/shared/utils/environments';
import { FQS_HOTSTORAGE_REQUEST_OPTIONS } from '@ctw/shared/utils/hotstorage-header';
import { QUERY_KEY_PATIENT_VITALS } from '@ctw/shared/utils/query-keys';
import { ValueSets, VitalsValueSets } from '@ctw/shared/utils/value-sets';
import type { GraphQLClient } from 'graphql-request';
import { compact, groupBy, orderBy, uniq } from 'lodash-es';
import { getLensEncountersFQS } from './encounters';
import { VitalsBucket } from './models/vitals-bucket';
import { referenceToId } from './resource-helper';

// Returns an array of EncounterModelWithVitals, each with an array of vitals.
export const usePatientVitals = (enabled = true) => {
  return usePatientQuery({
    queryId: QUERY_KEY_PATIENT_VITALS,
    enabled,
    queryFn: async ({ requestContext, telemetry, graphqlClient, patient }) => {
      const allVitalsLoincCodes = VitalsValueSets.flatMap(
        (setName) =>
          ValueSets[setName]?.content
            .filter((code) => code.system === SYSTEM_LOINC)
            .map((code) => code.code) || [],
      );

      const vitals = (
        await fetchObservationsByLoinc(
          telemetry,
          graphqlClient,
          patient,
          allVitalsLoincCodes,
          FQS_HOTSTORAGE_REQUEST_OPTIONS,
        )
      ).filter((vital) => vital.valueWithUnits !== '' || !!vital.resource.component);
      if (vitals.length === 0) {
        return [];
      }

      const encounters = await fetchVitalEncounters(
        vitals,
        telemetry,
        graphqlClient,
        patient,
        requestContext.env,
      );

      return createVitalBuckets(vitals, requestContext.builderId, encounters);
    },
  });
};

// Observations reference their SOURCE encounter's id, but the Lens summary that supersedes it
// has its own id — so the match has to happen client-side against the summary's
// `aggregatedFromIds` (FQS has no server-side filter on that extension).
async function fetchVitalEncounters(
  vitals: Array<ObservationModel>,
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
  env: Env,
): Promise<Array<EncounterModel>> {
  const encounterRefs = uniq(
    vitals.map((observation) => observation.resource.encounter?.reference),
  );
  const encounterIds = compact(encounterRefs.map(referenceToId));
  if (encounterIds.length === 0) {
    return [];
  }

  const lensEncounters = await getLensEncountersFQS(telemetry, graphqlClient, patient, env);
  return lensEncounters.filter((encounter) =>
    encounter.aggregatedFromIds.some((sourceEncounterId) =>
      encounterIds.includes(sourceEncounterId),
    ),
  );
}

// Group all vitals by date and grab the most recent encounter for each date.
// This way we handle a few different cases:
// 1. Duplicate encounters for the same date. We'll add all of their vitals to a single bucket
//    and take the average of the matching vitals thus cancelling out the duplications.
// 2. Vitals without encounters will still be grouped by date and displayed in the UI.
// 3. Different encounters on the same date will be displayed together, using the most recent
//    encounter for that date.
function createVitalBuckets(
  vitals: Array<ObservationModel>,
  builderId: string,
  encounters: Array<EncounterModel>,
) {
  const vitalsByDate = groupBy(vitals, (observation) => observation.dateDisplay);
  const encountersByDate = groupBy(encounters, 'periodStart');

  return Object.entries(vitalsByDate).map(([date, dateVitals]) => {
    // Grab the most recent encounter that has a type.
    const orderedEncounters = orderBy(
      encountersByDate[date],
      (e) => e.resource.period?.start,
      'desc',
    );
    const encounter =
      orderedEncounters.find((e) => e.typeDisplay !== 'Unknown') ?? orderedEncounters[0];
    return new VitalsBucket(dateVitals, builderId, encounter);
  });
}
